import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { isArray, uniqueId } from 'lodash'
import { FiChevronDown } from 'react-icons/fi'

const Select = ( { options, onChange,selectLabel, selected, multiple, noSelectedLabel, style }:Props ) => {

  const [ isOpen, setIsOpen ]     = useState<boolean>( false )
  const [ search, setSearch ] = useState<string>( '' )
  const [ resetSearch, setResetSearch ] = useState<boolean>( false )
  const [ writing, setWriting ] = useState<any>( false )

  const ref = useRef<any>( null )
  const searchedOption = useRef<any>( null )
  const optionsList = useRef<any>( null )

  const label = useMemo( () => {
    if( search.length > 0 ) return search
    if( multiple && isArray(selected) && selected.length < 1 ) return noSelectedLabel ??  selectLabel ??  'Selecione'
    if( !selected ) return  noSelectedLabel ?? selectLabel ?? 'Selecione'
    return !isArray(selected) ? options?.find( o => _.isEqual(o.value, selected ) )?.label :
      options?.filter( o =>
          selected.find( s =>  _.isEqual( s, o.value ) ) !== undefined
        )
        .map( o => o.label )
        .join( ', ' )
  }, [ selected, multiple, noSelectedLabel,selectLabel, options, search ] )


  const handleOutsideClick = useCallback( ( e:MouseEvent ) => {
    if( !ref.current  ) return
    if( !e.composedPath().includes( ref.current ) ) setIsOpen( false )
  }, [ ref ] )

  const handleOptionClick = useCallback( ( option:Option ) => {
    setSearch( '' )
    setResetSearch( true )
    if( !multiple ) {
      setIsOpen( false )
      return onChange && onChange( option )
    }
    if( selected.find( ( s:any ) =>  _.isEqual( s, option.value ) ) !== undefined ){
      onChange && onChange( selected.filter( ( s:any ) => ! _.isEqual( s, option.value ) ) )
    }else{
      onChange && onChange( [ ...selected, option.value ] )
    }
  }, [ onChange, multiple, selected ] )

  const handleTyping = useCallback( ( e:KeyboardEvent ) => {
    if( !isOpen || ( !options || options.length < 1 ) ) return

    if( writing ) setWriting( ( state:any ) => {
      clearTimeout( state )
      resetSearch && setResetSearch( false )
    } )
    setWriting( setTimeout( () => setResetSearch( true ), 1000 )  )

    setSearch( search => {
      if( e.key.match( /[a-z0-9 ]{1}$/gi ) && e.key.length === 1 ) {
        if( resetSearch ) return e.key
        else return search + e.key
      }
      if( e.key.toLowerCase() === 'backspace' ) return search.slice(0, -1)

      if( search.length > 0 && e.key.toLowerCase() === 'enter' ){
        const hasOption = options?.find( option => {
          return option.label.slice(0, search.length ).toLowerCase().includes( search.toLowerCase() )
        })

        if( hasOption ) handleOptionClick( hasOption )
      }
      return search
    })

  }, [ isOpen, resetSearch, writing, options, handleOptionClick ] )

  useEffect( ( ) => {
    if( !searchedOption.current ) return
    if( !optionsList ) return
    optionsList.current.scrollTop = searchedOption.current.offsetTop - 20;
  }, [ searchedOption, optionsList, search ] )


  useEffect( () => {
    document.addEventListener( 'keydown', handleTyping )
    return () => {
      document.removeEventListener( 'keydown', handleTyping )
    }
  }, [ handleTyping  ])

  useEffect( () => {
    document.addEventListener( 'click', handleOutsideClick )
    return () => {
      document.removeEventListener( 'click', handleOutsideClick )
    }
  }, [ handleOutsideClick  ])

  return (
    <div
      ref={ ref }
      style={ style }
      className="w-full h-10 rounded flex items-center relative bg-white"
    >
      <div
        className={[
          "h-full w-full flex items-center cursor-pointer select-none pl-2",
          "overflow-auto text-base whitespace-nowrap border rounded-md",
          !isOpen ? '' : 'border-b-0 rounded-b-none'
        ].join(' ')}
        onClick={ () => { setIsOpen( isOpen => !isOpen ) } }
      >
        { label }
        <span className={
          [
            "pointer-events-none absolute right-2 top-0 h-full flex items-center",
            "text-orange text-xl -ml-8 overfloww-visible",
            "duration-100",
            !isOpen ? '' : 'transform rotate-180'
          ].join(' ')
        }
        >
          <FiChevronDown/>
        </span>
      </div>
      <div
        ref={ optionsList }
        className={
          [
            "absolute top-full rounded-b-md left-0 z-50 w-full",
            "flex flex-col justify-start duration-100 border-t-0 bg-white",
            isOpen ? 'border -mt-1' : '',
            isOpen ? 'max-h-72 overflow-auto' : 'max-h-0 overflow-hidden'
          ].join(' ')
        }
      >
        { options && options.map( option => (
          <div
            key={ uniqueId() } onClick={ () => handleOptionClick( option ) }
            ref={ search.length > 0 && option.label
              .slice(0, search.length )
              .toLowerCase()
              .includes( search.toLowerCase() ) ? searchedOption : null
            }
            className={
              [
                "p-1.5 w-full max-w-full cursor-pointer inline-flex items-center",
                "flex-1 last:border-0",
                search.length > 0 && option.label
                  .slice(0, search.length )
                  .toLowerCase()
                  .includes( search.toLowerCase() ) ? 'bg-orange' : ''
              ].join(' ')
            }
          >
            { !multiple ? option.label :
            <div>
              <label className='text-lg'> 
                <input
                  defaultChecked={ selected === option.value ||
                    selected.find( (s:any) => _.isEqual(  s, option.value  ) ) !== undefined}
                  type="checkbox"
                  className='text-xl mr-2'
                />
                { option.label }
              </label>
             </div>
            }
          </div>
        ) ) }
      </div>
    </div>
  )
}

export interface Option {
  value: any,
  label:string,
}

interface Props{
  options?: Option[],
  selected?: any | any[]
  multiple?: boolean
  onChange?: ( item:any | any[]  ) => any,
  noSelectedLabel?: string,
  style?: CSSProperties,
  selectLabel?:String
}

export { Select }
